// Must match the PWA's token-store.ts byte-for-byte — this reads the SAME same-origin store.
const DB_NAME = 'vn-pwa-auth'
const DB_VERSION = 1
const STORE_NAME = 'tokens'
const TOKEN_KEY = 'bearer'

/** Open (creating the store if this origin somehow reaches the dashboard before the PWA wrote it). */
const openDb = (factory: IDBFactory): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = (): void => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME)
    }
    request.onsuccess = (): void => resolve(request.result)
    request.onerror = (): void => reject(request.error)
  })

/** Run one request against the token store and resolve with its result. */
const runInStore = <T>(
  factory: IDBFactory,
  mode: IDBTransactionMode,
  operate: (store: IDBObjectStore) => IDBRequest,
): Promise<T> =>
  openDb(factory).then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const request = operate(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME))
        request.onsuccess = (): void => {
          resolve(request.result as T)
          db.close()
        }
        request.onerror = (): void => {
          reject(request.error)
          db.close()
        }
      }),
  )

/** The bearer the PWA saved, or null. Falls back to null on any storage error (blocked → bounce). */
export const readBearerToken = (factory: IDBFactory = indexedDB): Promise<string | null> =>
  runInStore<string | undefined>(factory, 'readonly', (store) => store.get(TOKEN_KEY))
    .then((value) => value ?? null)
    .catch(() => null)

/** Forget the token (logout / 401), then the caller bounces to the PWA to re-authenticate. */
export const clearBearerToken = (factory: IDBFactory = indexedDB): Promise<void> =>
  runInStore<undefined>(factory, 'readwrite', (store) => store.delete(TOKEN_KEY))
    .then(() => undefined)
    .catch(() => undefined)
